import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { UpsellingModalConfig } from '@front-platform/content-builder';
import { resolveCloseIconFieldColors, upsellingModalResolveConfig } from '@front-platform/content-builder';
import { map } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ModalsService } from '@core/services/modals/modals.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { UpsellingStoreSelector } from '@common/selectors/upselling.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import {
  KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX,
  KioskProductOverviewModalComponent,
  KioskProductOverviewModalConfig,
} from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { staggeredListAnimation } from '@shared/animations/list.animation';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { CloseModalOnBrowserBack } from '@core/services/modal-back-history/modal-close-on-back.decorator';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { KioskProductCardFactoryComponent } from '@shared/atoms/kiosk/kiosk-product-cards/kiosk-product-card-factory/kiosk-product-card-factory.component';
import { ProductCardAtomComponent } from '@shared/atoms/product-card-atom/product-card-atom.component';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveUpsellingModalPreviewContent } from './upselling-modal-preview.util';
import { resolveUpsellingProductsLayout } from './upselling-products-layout';

export type UpsellingModalModalConfig = ModalConfig<{
  handle: typeof UpsellingModalComponent.handle;
  inputs: {
    triggerProductId: string;
    categoryId?: string;
    textColor?: IContentBuilderFieldColor;
    cardColor?: IContentBuilderFieldColor;
    buttonBackgroundColor?: IContentBuilderFieldColor;
    unavailableButtonBackground?: IContentBuilderFieldColor;
    unavailableButtonText?: IContentBuilderFieldColor;
    buttonTextColor?: IContentBuilderFieldColor;
    headingColor?: IContentBuilderFieldColor;
    closeIconFieldColors?: ContentBuilderFieldColors;
  };
  outputs:
    | {
        type: 'selectedProposal';
        selectedProposal: DoodProductModel;
        triggerProductId: string;
        upsellingId: string;
      }
    | {
        type: 'closed';
        triggerProductId: string;
      };
}>;
export interface UpsellingModalRuntimeConfig {
  'trigger_product_id'?: string;
  'category_id'?: string;
  'text_color'?: IContentBuilderFieldColor;
  'card_color'?: IContentBuilderFieldColor;
  'button_background_color'?: IContentBuilderFieldColor;
  'unavailable_button_background'?: IContentBuilderFieldColor;
  'unavailable_button_text'?: IContentBuilderFieldColor;
  'button_text_color'?: IContentBuilderFieldColor;
  'heading_color'?: IContentBuilderFieldColor;
  'close_icon_field_colors'?: ContentBuilderFieldColors;
}

export type UpsellingModalConfigInput = Partial<
  Omit<UpsellingModalConfig, keyof UpsellingModalRuntimeConfig> & UpsellingModalRuntimeConfig
>;
export type UpsellingModalResolvedConfig = Omit<
  UpsellingModalConfig,
  keyof UpsellingModalRuntimeConfig
> &
  Partial<UpsellingModalRuntimeConfig>;
export interface UpsellingModalContract extends ConfigComponentContract<
  UpsellingModalConfigInput,
  UpsellingModalResolvedConfig
> {}

@CloseModalOnBrowserBack()
@Component({
  selector: 'app-upselling-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [staggeredListAnimation],
  imports: [
    ModalFillerAtomComponent,
    KioskProductCardFactoryComponent,
    ButtonAtomComponent,
    ProductCardAtomComponent,
    ContentBuilderColorPipe,
    TranslatePipe,
  ],
  template: `
    <app-modal-filler-atom
      (onClick)="close()"
      [index]="5"
    ></app-modal-filler-atom>
    <div
      data-testid="upselling-modal"
      class="fixed bottom-0 left-0 right-0 mx-auto max-h-85-screen w-full overflow-y-auto md:top-0 md:flex md:h-full md:items-center md:justify-center"
      [style.z-index]="750"
    >
      @if (isDesktop()) {
        <app-modal-filler-atom
          [withBackdrop]="false"
          (onClick)="close()"
          [index]="index()"
        ></app-modal-filler-atom>
      }
      <div
        class="flex w-full flex-col gap-6 space-y-5 rounded-t-3xl bg-white p-5 text-center md:rounded-3xl kiosk:px-16"
        [class.md:max-w-2xl]="!productsLayout().wideModal"
        [class.md:max-w-4xl]="productsLayout().wideModal"
        [style.z-index]="index() + 50"
      >
        <div class="flex items-center">
          <div
            class="flex flex-1 items-center"
            [style.color]="headingColor() | contentBuilderColor: '#000'"
          >
            <span class="font-brand font-bold text-2xl kiosk:text-3.5xl">{{
              upsellingTitle() || ('upselling.modal-title' | translate)
            }}</span>
          </div>
          <button
            (click)="close()"
            class="icon icon-cross h-8 w-8 rounded-full text-xs kiosk:h-16 kiosk:w-16 kiosk:text-3xl"
            [style.backgroundColor]="
              closeIconFieldColors()?.background
                | contentBuilderColor: 'var(--ui-color-primary-200)'
            "
            [style.color]="
              closeIconFieldColors()?.font | contentBuilderColor: 'var(--ui-color-primary-900)'
            "
          ></button>
        </div>

        <!-- Description -->
        @if (upsellingDescription()) {
          <div
            class="text-left"
            [style.color]="textColor() | contentBuilderColor: '#000'"
          >
            <p class="text-base kiosk:text-2.5xl">{{ upsellingDescription() }}</p>
          </div>
        }

        <div
          class="flex flex-col gap-5 overflow-scroll scrollbar-hide"
          [@staggeredListAnimation]="{
            value: proposalsProducts().length,
            params: { animateEnterElementFromTopToBottom: true },
          }"
        >
          @let layout = productsLayout();
          <div
            data-testid="upselling-products"
            class="grid items-stretch gap-5 overflow-y-scroll p-1 scrollbar-hide [grid-auto-rows:minmax(0,1fr)_auto]"
            [class.justify-center]="layout.centerSingleCard"
            [style.gridTemplateColumns]="layout.gridTemplateColumns"
          >
            @for (product of proposalsProducts(); track product.id; let index = $index) {
              <div
                class="row-span-2 grid min-h-0 grid-rows-subgrid gap-3"
                [class.mx-auto]="layout.centerSingleCard"
                [class.w-full]="layout.centerSingleCard"
                [class.max-w-[365px]]="layout.centerSingleCard"
              >
                <!-- min-h-0 + overflow clip: kiosk card has h-full; without this the card paints over the CTA row -->
                <div class="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  @if (!isMobile()) {
                    @if (product) {
                      <app-kiosk-product-card-factory
                        [product]="product"
                        [quantity]="product.quantity"
                        (addProduct)="addProductDispatcher(product)"
                        (showProduct)="openProductModal(product)"
                        [textColor]="textColor()"
                        [cardColor]="cardColor()"
                        [isProductAvailable]="
                          !!product.available && !unAvailableProductById()[product.id]
                        "
                        [buttonBackgroundColor]="buttonBackgroundColor()"
                        [unavailableButtonBackground]="unavailableButtonBackground()"
                        [unavailableButtonText]="unavailableButtonText()"
                        [buttonTextColor]="buttonTextColor()"
                        class="min-h-0 flex-1"
                      ></app-kiosk-product-card-factory>
                    }
                  } @else {
                    <app-product-card-atom
                      [product]="product"
                      (click)="selectProposal(product)"
                      [displayProductLabels]="{ displayLabels: false }"
                      class="min-h-0 flex-1"
                    />
                  }
                </div>

                <!-- Proposal button if title exists -->
                @if (getProposalTitle(product.id)) {
                  <app-button-atom
                    class="relative z-10 block w-full shrink-0 [&>button]:min-h-14 kiosk:[&>button]:min-h-20 [&>button]:whitespace-normal"
                    [size]="'L'"
                    thickSize="L"
                    [label]="getProposalTitle(product.id)!"
                    [type]="index === 0 ? 'kiosk-light' : 'kiosk-primary'"
                    (click)="selectProposal(product)"
                    [full]="true"
                  ></app-button-atom>
                }
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class UpsellingModalComponent
  implements GetModalInterface<UpsellingModalModalConfig>, UpsellingModalContract
{
  readonly config = input<UpsellingModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    UpsellingModalConfig,
    UpsellingModalRuntimeConfig
  >(upsellingModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'upselling-modal' as const;

  private readonly upsellingStoreSelector = inject(UpsellingStoreSelector);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBlocKey = inject(CB2_PREVIEW_BLOC_KEY, { optional: true });
  private readonly previewContent = computed(() =>
    resolveUpsellingModalPreviewContent({
      isPreview: this.cb2PreviewState.isActive(),
      scenarios: this.cb2PreviewState.previewScenarios(),
      blocKey: this.previewBlocKey,
    }),
  );
  private readonly productDispatcher = inject(ProductStoreDispatcher);
  private readonly modalsService: ModalsService<UpsellingModalModalConfig> = inject(
    ModalsService<UpsellingModalModalConfig>,
  );

  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);
  protected readonly isKiosk = this.settingsStoreSelector.deviceIsKiosk;
  protected readonly isMobile = this.settingsStoreSelector.deviceIsMobileScreen;

  protected readonly proposalButtonTextSize = computed(() => {
    if (this.isKiosk()) {
      return 'XL';
    }
    if (this.isDesktop()) {
      return 'L';
    }
    return 'L';
  });

  protected readonly index = toSignal(
    this.modalsService.getData('upselling-modal').pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );

  readonly triggerProductId = computed(
    () => this.resolvedConfig()['trigger_product_id'] as unknown as string,
  );
  readonly categoryId = computed(() => this.resolvedConfig()['category_id'] as unknown as string);
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonBackground = computed(
    () =>
      this.resolvedConfig()[
        'unavailable_button_background'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonText = computed(
    () => this.resolvedConfig()['unavailable_button_text'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  public readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  public readonly closeIconFieldColors = computed(() => {
    const cfg = this.resolvedConfig() as Record<string, unknown>;
    return resolveCloseIconFieldColors(cfg, this.config() as Record<string, unknown>);
  });

  protected readonly proposalsProducts = computed(() => {
    const preview = this.previewContent();
    if (preview) {
      return [...preview.products] as unknown as DoodProductModel[];
    }
    return this.upsellingStoreSelector.selectProposalsByTriggerProductId(this.triggerProductId())();
  });

  protected readonly upsellingMapping = computed(() =>
    this.upsellingStoreSelector.selectMappingByTriggerProductId(this.triggerProductId())(),
  );

  protected readonly upsellingTitle = computed(() => {
    const preview = this.previewContent();
    if (preview) {
      return preview.title || null;
    }
    return this.upsellingMapping()?.title || null;
  });

  protected readonly upsellingDescription = computed(() => {
    const preview = this.previewContent();
    if (preview) {
      return preview.description || null;
    }
    return this.upsellingMapping()?.description || null;
  });

  protected readonly proposalsWithTitles = computed(() => {
    const preview = this.previewContent();
    if (preview) {
      return preview.products.map(product => ({
        productId: product.id,
        title: preview.proposalLabels[product.id] ?? '',
      }));
    }
    return this.upsellingMapping()?.proposals || [];
  });

  protected readonly productsLayout = computed(() =>
    resolveUpsellingProductsLayout({
      productCount: this.proposalsProducts()?.length ?? 0,
      productCardChoice: this.kioskGlobalSettingsService.productCardChoice(),
      productCardColSpan: this.kioskGlobalSettingsService.productCardColSpan(),
      showsKioskProductCard: !this.isMobile(),
    }),
  );

  getProposalTitle(productId: string): string | null {
    const proposals = this.proposalsWithTitles();
    const proposal = proposals.find(p => p.productId === productId);
    return proposal?.title || null;
  }

  selectProposal(product: DoodProductModel): void {
    this.addProductDispatcher(product);
  }

  addProductDispatcher(product: DoodProductModel): void {
    const mapping = this.upsellingMapping();
    this.modalsService.emitEvent({
      handle: 'upselling-modal',
      data: {
        type: 'selectedProposal',
        selectedProposal: product,
        triggerProductId: this.triggerProductId(),
        upsellingId: mapping!.upsellingId,
      },
    });
    this.closeWithoutEvent();
  }

  openProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open<KioskProductOverviewModalConfig>(
      KioskProductOverviewModalComponent.handle,
      undefined,
      {
        image: product.images?.cover?.url,
        name: product.name,
        description: product.description,
        allergens: product.allergens,
        type: product.type,
        zIndex: KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX,
        colors: {
          background: this.buttonTextColor(),
          accent: this.buttonBackgroundColor(),
          closeButton: this.buttonBackgroundColor(),
        },
      },
    );
  }

  close(): void {
    this.modalsService.emitEvent({
      handle: 'upselling-modal',
      data: {
        type: 'closed',
        triggerProductId: this.triggerProductId(),
      },
    });
    this.closeWithoutEvent();
  }

  private closeWithoutEvent(): void {
    this.modalsService.close(UpsellingModalComponent.handle);
  }

  protected readonly unAvailableProductById = computed(() => {
    const errors = this.orderStoreSelector.selectErrorsSignal();
    return errors.violations
      ?.filter(
        violation =>
          violation.code &&
          [
            'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
            'PRODUCT_HIDDEN',
            'PRODUCT_NOT_AVAILABLE',
          ].includes(violation.code),
      )
      .map(violation => violation.data?.id as string)
      .reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
  });
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(UpsellingModalComponent, { 'keyAliases': {} });
